import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { Button, Modal } from '@components/index';
import {
  KnowledgeAttribute,
  knowledgeAttributesAtom,
} from '@store/atoms/knowledge-attributes';

import ExpandableAttributeSection from './ExpandableAttributeSection';

interface CustomAttributeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExtract: (selectedAttributeIds: number[]) => void;
  fileCount: number;
}

// Manually-entered attributes stay in the add-attribute modal (that is how their
// values get set) but must never be submitted here: the extractor has no prompt
// for them and fails the document if a selection contains nothing else.
// Attributes with no recognised group are dropped too — this modal renders by
// group, so there is nowhere to put them.
const selectableAttributes = (attributes: KnowledgeAttribute[]) =>
  attributes.filter(attr => attr.isExtractable && attr.attributeGroup);

const groupAttributesByGroup = (attributes: KnowledgeAttribute[]) => {
  return attributes.reduce(
    (acc, attr) => {
      const group = attr.attributeGroup;
      if (!group) {
        return acc;
      }
      if (!acc[group.key]) {
        acc[group.key] = { group, attributes: [] };
      }
      acc[group.key].attributes.push(attr);

      return acc;
    },
    {} as Record<
      string,
      {
        group: NonNullable<KnowledgeAttribute['attributeGroup']>;
        attributes: KnowledgeAttribute[];
      }
    >,
  );
};

const CustomAttributeSelectionModal: FC<CustomAttributeSelectionModalProps> = ({
  isOpen,
  onClose,
  onExtract,
  fileCount,
}) => {
  const knowledgeAttributes = useRecoilValue(knowledgeAttributesAtom);

  // Grouping, selection and the button count all derive from this list.
  const selectableList = useMemo(
    () => selectableAttributes(knowledgeAttributes),
    [knowledgeAttributes],
  );

  // Grouped and memoized attributes
  const sections = useMemo(() => {
    const grouped = groupAttributesByGroup(selectableList);

    return Object.values(grouped);
  }, [selectableList]);

  // States
  const [expandedSectionIndex, setExpandedSectionIndex] = useState<
    number | null
  >(null);

  const [selectedAttributes, setSelectedAttributes] = useState<Set<number>>(
    new Set(selectableList.map(attr => attr.id)),
  );

  useEffect(() => {
    if (selectableList.length > 0) {
      setSelectedAttributes(new Set(selectableList.map(attr => attr.id)));
    }
  }, [selectableList]);

  // Handlers
  const toggleSection = (index: number) => {
    setExpandedSectionIndex(prev => (prev === index ? null : index));
  };

  const toggleAttribute = (attributeId: number) => {
    setSelectedAttributes(prev => {
      const updated = new Set(prev);
      updated.has(attributeId)
        ? updated.delete(attributeId)
        : updated.add(attributeId);

      return updated;
    });
  };

  const deselectAll = () => setSelectedAttributes(new Set());

  const handleClose = () => {
    // Reset state before closing
    setExpandedSectionIndex(null);
    setSelectedAttributes(new Set(selectableList.map(attr => attr.id)));
    onClose();
  };

  const handleExtract = () => {
    onExtract?.(Array.from(selectedAttributes));
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="w-[1200px] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 pb-0 flex-shrink-0">
          <div className="flex flex-col gap-1">
            <div className="text-text-primary text-lg font-semibold">
              Select attributes to extract from uploaded documents
            </div>
            <div className="text-text-secondary text-sm">
              Select which sections to extract from your files.
            </div>
          </div>

          <div className="text-text-primary text-base font-medium my-6">
            You're about to extract {fileCount}{' '}
            {fileCount === 1 ? 'file' : 'files'}
          </div>

          <div className="mb-1 text-text-secondary text-sm">
            By default, all attributes are enabled{' '}
            <button onClick={deselectAll} className="text-black underline">
              Deselect all
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6">
          <div className="border border-gray-2 rounded-lg bg-white">
            {sections.map((section, index) => (
              <ExpandableAttributeSection
                key={section.group.key}
                sectionIndex={index}
                group={section.group}
                attributes={section.attributes}
                isExpanded={expandedSectionIndex === index}
                selectedAttributes={selectedAttributes}
                onToggleSection={toggleSection}
                onToggleAttribute={toggleAttribute}
              />
            ))}
          </div>

          {/* Footer */}
          <div className="py-10 flex-shrink-0">
            <div className="flex justify-end gap-3">
              <Button
                label="Cancel"
                variant="outlined"
                onClick={handleClose}
                classOverride="!w-[80px] !h-8"
              />
              <Button
                label={`Extract ${selectedAttributes.size} ${selectedAttributes.size === 1 ? 'attribute' : 'attributes'} from ${fileCount} ${fileCount === 1 ? 'file' : 'files'}`}
                variant="contained"
                onClick={handleExtract}
                classOverride="!w-[250px] !h-8"
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CustomAttributeSelectionModal;
